/**
 * Phase 18 — Multi-Dimensional Duplicate Invoice Detection Engine
 * Normalizes vendor invoice identifiers and evaluates exact vs potential duplicate signals
 * while preventing false duplicates across distinct vendors.
 */

import { DuplicateRisk, InvoiceRecord } from '@/types/domain/invoice-management';

/**
 * Normalizes an invoice reference string:
 * - Trims leading/trailing whitespace
 * - Converts to uppercase
 * - Normalizes common punctuation separators (/ \ - _) to hyphens
 * - Removes repeated separators
 */
export function normalizeInvoiceNumber(raw: string): string {
  if (!raw) return '';
  return raw
    .trim()
    .toUpperCase()
    .replace(/[/\\_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-\s]+|[-\s]+$/g, '');
}

export interface DuplicateDetectionResult {
  isDuplicate: boolean;
  duplicateRisk: DuplicateRisk;
  originalInvoiceId?: string;
  originalInvoiceNumber?: string;
  originalVendorInvoiceNumber?: string;
  reason?: string;
}

/**
 * Evaluates an incoming or existing invoice against the active invoice repository.
 */
export function detectDuplicateInvoice(
  candidate: {
    id?: string;
    vendorId: string;
    vendorInvoiceNumber: string;
    totalAmount: number;
    poId?: string;
    poNumber?: string;
    invoiceDate?: string;
  },
  existingInvoices: InvoiceRecord[]
): DuplicateDetectionResult {
  const normCandidateNumber = normalizeInvoiceNumber(candidate.vendorInvoiceNumber);
  if (!normCandidateNumber || !candidate.vendorId) {
    return { isDuplicate: false, duplicateRisk: 'NO_DUPLICATE' };
  }

  // Filter out the invoice itself if editing
  const otherInvoices = existingInvoices.filter((inv) => inv.id !== candidate.id);

  // 1. EXACT DUPLICATE CHECK: Same Vendor + Same Normalized Vendor Invoice Number
  const exactMatch = otherInvoices.find((inv) => {
    if (inv.status === 'CANCELLED') return false;
    const sameVendor = inv.vendorId === candidate.vendorId;
    const sameNormNumber = normalizeInvoiceNumber(inv.vendorInvoiceNumber) === normCandidateNumber;
    return sameVendor && sameNormNumber;
  });

  if (exactMatch) {
    return {
      isDuplicate: true,
      duplicateRisk: 'DUPLICATE_CONFIRMED',
      originalInvoiceId: exactMatch.id,
      originalInvoiceNumber: exactMatch.invoiceNumber,
      originalVendorInvoiceNumber: exactMatch.vendorInvoiceNumber,
      reason: `Exact duplicate detected: Vendor already submitted invoice #${exactMatch.vendorInvoiceNumber} under ProcureAI record ${exactMatch.invoiceNumber}.`,
    };
  }

  // 2. POTENTIAL DUPLICATE CHECK: Same Vendor + Same Amount + Same PO (different invoice number string)
  const potentialMatch = otherInvoices.find((inv) => {
    if (inv.status === 'CANCELLED') return false;
    const sameVendor = inv.vendorId === candidate.vendorId;
    const sameAmount = Math.abs(inv.totalAmount - candidate.totalAmount) < 0.01;
    const samePo = candidate.poId && inv.poId ? inv.poId === candidate.poId : false;
    return sameVendor && sameAmount && samePo;
  });

  if (potentialMatch) {
    return {
      isDuplicate: true,
      duplicateRisk: 'DUPLICATE_POTENTIAL',
      originalInvoiceId: potentialMatch.id,
      originalInvoiceNumber: potentialMatch.invoiceNumber,
      originalVendorInvoiceNumber: potentialMatch.vendorInvoiceNumber,
      reason: `Potential duplicate flagged: Identical billed amount (${candidate.totalAmount}) submitted for PO #${candidate.poNumber || potentialMatch.poNumber} under invoice #${potentialMatch.vendorInvoiceNumber}.`,
    };
  }

  // 3. DIFFERENT VENDOR CHECK:
  // If same invoice number belongs to a DIFFERENT vendor, it is NOT a duplicate.
  return {
    isDuplicate: false,
    duplicateRisk: 'NO_DUPLICATE',
  };
}
